import { useState } from "react"

import { Button } from "@coval/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@coval/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { SettingRow } from "@coval/ui/setting-row"

import { useUploadedAudioSettingsTransport } from "../transport"

export function UploadedAudioSettings({ canManage }: { canManage: boolean }) {
  const { query, mutation, invalidate } = useUploadedAudioSettingsTransport()
  const [error, setError] = useState<string | null>(null)
  const channel = query.data?.settings?.default_agent_channel === 1 ? "1" : "0"

  async function save(value: string) {
    if (!canManage || mutation.isPending || (value !== "0" && value !== "1")) {
      return
    }
    setError(null)
    try {
      await mutation.mutateAsync({ default_agent_channel: Number(value) })
    } catch {
      setError(
        "Could not save the agent channel. Check your permissions and try again.",
      )
    } finally {
      await invalidate()
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Uploaded conversations</CardTitle>
      </CardHeader>
      <CardContent>
        {query.error && !query.data ? (
          <div role="alert">
            <p>Could not load uploaded audio settings.</p>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <SettingRow
            label="Agent channel"
            tooltip="The agent's channel in uploaded stereo audio. API overrides take precedence."
            error={error}
          >
            {(id, describedBy) => (
              <Select
                value={channel}
                onValueChange={(value) => void save(value)}
                disabled={!canManage || !query.data || mutation.isPending}
              >
                <SelectTrigger
                  id={id}
                  aria-label="Agent channel"
                  {...describedBy}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Left (0)</SelectItem>
                  <SelectItem value="1">Right (1)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </SettingRow>
        )}
        {!canManage && (
          <p className="text-sm text-coval-text-muted">
            An admin can change this setting.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
